import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FlagIcon from './FlagIcon';

interface Language {
  code: 'fr' | 'en' | 'es' | 'zh';
  name: string;
  nativeName: string;
}

const LANGUAGES: Language[] = [
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
];

const LanguageSelector: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { i18n } = useTranslation();

  const selectedLanguage =
    LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

  const handleLanguageSelect = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:border-gray-400 transition-all text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <FlagIcon country={selectedLanguage.code} className="w-5 h-5" />
        <span className="text-sm font-medium text-gray-700 hidden sm:inline">
          {selectedLanguage.nativeName}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute top-full right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 min-w-[160px]">
            {LANGUAGES.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageSelect(language.code)}
                className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${
                  language.code === i18n.language ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  <FlagIcon country={language.code} className="w-5 h-5" />
                  <div>
                    <div className="font-medium text-sm text-gray-900">
                      {language.nativeName}
                    </div>
                    <div className="text-xs text-gray-600">{language.name}</div>
                  </div>
                  {language.code === i18n.language && (
                    <svg
                      className="w-4 h-4 text-blue-500 ml-auto"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
